/**
 * Settings (/settings): email notification preferences, per tenant, on
 * mockup carbon/01's brief and carbon/02's fields.
 *
 *   1. The brief (BriefHero): an action-first h1 from the saved state
 *      (consent that is missing or expired first, then tenants whose drift
 *      email is off), and the view's one primary button for that tenant.
 *   2. One disclosure per tenant: your drift email (Toggle, the format as a
 *      tile RadioGroup, an override address), permissions and consent, and
 *      the tenant's shared service-desk addresses. Each block saves on its
 *      own, with a secondary button.
 *   3. A note on the two layers of notification settings.
 */
import { useState, useEffect, useId, type ReactNode } from 'react';
import type { Tenant } from '@/types';
import { api, AdminConsentStatus } from '@/lib/api';
import { BriefHero } from '@/components/carbon/BriefHero';
import { Button } from '@/components/carbon/Button';
import { InlineNotification } from '@/components/carbon/InlineNotification';
import { RadioGroup } from '@/components/carbon/RadioGroup';
import { Skeleton } from '@/components/carbon/Skeleton';
import { Tag } from '@/components/carbon/Tag';
import { TextInput } from '@/components/carbon/TextInput';
import { Tile } from '@/components/carbon/Tile';
import { TileHeader } from '@/components/carbon/TileHeader';
import { Toggle } from '@/components/carbon/Toggle';
import { IconArrowRight, IconCheck, IconChevronDown, IconCopy, IconExternal } from '@/components/carbon/icons';
import { ActionFeedback } from '@/components/AdminPortal/drawer-parts';
import { formatLongDate } from '@/components/AdminPortal/admin-format';
import { composeSettingsBrief } from '@/components/AdminPortal/page-briefs';

interface TenantEmailPreferences {
  tenantId: string;
  driftNotificationsEnabled: boolean;
  driftNotificationEmail: string;
  driftNotificationFormat: string;
  updatedAt: string;
  isGlobal: boolean;
}

interface TenantNotificationSettings {
  tenantId: string;
  rejectedDriftNotificationEmail?: string;
  driftAlertNotificationEmail?: string;
}

const FORMAT_OPTIONS = [
  {
    value: 'AI-Enhanced',
    label: 'AI summary',
    helper: 'Changes grouped by severity with a business-impact summary for each. Recommended for most tenants.',
  },
  {
    value: 'FullDigest',
    label: 'Full digest',
    helper: 'Every pending drift as a numbered list, with technical detail and a short AI summary each.',
  },
];

/** A titled block inside a tenant's settings (14px/600), under a border-subtle rule after the first. */
function SettingsBlock({ title, first = false, children }: { title: string; first?: boolean; children: ReactNode }) {
  const id = useId();
  return (
    <section aria-labelledby={id} className={first ? undefined : 'border-t border-line-subtle pt-6'}>
      <h4 id={id} className="m-0 mb-4 text-body font-semibold text-text-primary">
        {title}
      </h4>
      {children}
    </section>
  );
}

export default function Settings() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantPreferences, setTenantPreferences] = useState<TenantEmailPreferences[]>([]);
  // State value isn't read directly — the setter is the source of truth for per-tenant
  // notification overrides; UI reads through the form-data state below.
  const [, setTenantNotificationSettings] = useState<Record<string, TenantNotificationSettings>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [savingTenantSettings, setSavingTenantSettings] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [expandedTenants, setExpandedTenants] = useState<Set<string>>(new Set());
  const [consentStatuses, setConsentStatuses] = useState<Record<string, AdminConsentStatus>>({});
  const [consentLoading, setConsentLoading] = useState<string | null>(null);
  const [copiedTenantId, setCopiedTenantId] = useState<string | null>(null);

  // Form state for each tenant (user preferences)
  const [formData, setFormData] = useState<Record<string, {
    driftNotificationsEnabled: boolean;
    driftNotificationEmail: string;
    driftNotificationFormat: string;
  }>>({});

  // Form state for tenant-level notification settings
  const [tenantSettingsFormData, setTenantSettingsFormData] = useState<Record<string, {
    rejectedDriftNotificationEmail: string;
    driftAlertNotificationEmail: string;
  }>>({});

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load tenants and preferences in parallel
      const [tenantsData, preferencesData] = await Promise.all([
        api.getTenants(),
        api.getAllTenantEmailPreferences()
      ]);

      setTenants(tenantsData);

      // Filter out any global preferences (TenantId='*') - no longer supported
      const tenantPrefs = preferencesData.filter(p => p.tenantId !== '*');

      setTenantPreferences(tenantPrefs);

      // Initialize form data for all tenants
      const initialFormData: Record<string, any> = {};

      // Tenant-specific preferences (existing + defaults for missing tenants)
      tenantsData.forEach(tenant => {
        const existing = tenantPrefs.find(p => p.tenantId === tenant.tenantId);
        initialFormData[tenant.tenantId] = {
          driftNotificationsEnabled: existing?.driftNotificationsEnabled ?? false,
          driftNotificationEmail: existing?.driftNotificationEmail ?? '',
          driftNotificationFormat: existing?.driftNotificationFormat ?? 'AI-Enhanced'
        };
      });

      setFormData(initialFormData);

      // Load tenant-level notification settings for each tenant
      const tenantSettingsPromises = tenantsData.map(async (tenant) => {
        try {
          const settings = await api.getTenantNotificationSettings(tenant.tenantId);
          return { tenantId: tenant.tenantId, settings };
        } catch {
          // If settings don't exist, return defaults
          return { tenantId: tenant.tenantId, settings: { tenantId: tenant.tenantId, rejectedDriftNotificationEmail: undefined, driftAlertNotificationEmail: undefined } };
        }
      });

      const tenantSettingsResults = await Promise.all(tenantSettingsPromises);
      const initialTenantSettings: Record<string, TenantNotificationSettings> = {};
      const initialTenantSettingsFormData: Record<string, { rejectedDriftNotificationEmail: string; driftAlertNotificationEmail: string }> = {};

      tenantSettingsResults.forEach(({ tenantId, settings }) => {
        initialTenantSettings[tenantId] = settings;
        initialTenantSettingsFormData[tenantId] = {
          rejectedDriftNotificationEmail: settings.rejectedDriftNotificationEmail || '',
          driftAlertNotificationEmail: settings.driftAlertNotificationEmail || ''
        };
      });

      setTenantNotificationSettings(initialTenantSettings);
      setTenantSettingsFormData(initialTenantSettingsFormData);

      // Load consent status for each tenant (for reconsent section)
      const consentPromises = tenantsData.map(async (tenant) => {
        try {
          const status = await api.getAdminConsentStatus(tenant.tenantId);
          return { tenantId: tenant.tenantId, status };
        } catch {
          return { tenantId: tenant.tenantId, status: null };
        }
      });
      const consentResults = await Promise.all(consentPromises);
      const consentMap: Record<string, AdminConsentStatus> = {};
      consentResults.forEach(({ tenantId, status }) => {
        if (status) consentMap[tenantId] = status;
      });
      setConsentStatuses(consentMap);

    } catch (err) {
      console.error('Failed to load settings data:', err);
      setError('Settings did not load. Refresh to try again.');
    } finally {
      setLoading(false);
    }
  };

  const saveTenantPreferences = async (tenantId: string) => {
    try {
      setSaving(prev => ({ ...prev, [tenantId]: true }));
      setError(null);
      setSuccess(null);

      const data = formData[tenantId];
      if (!data) return;

      // Update tenant-specific preferences
      const updatedPreferences = await api.updateTenantEmailPreferences(tenantId, {
        driftNotificationsEnabled: data.driftNotificationsEnabled,
        driftNotificationEmail: data.driftNotificationEmail || undefined,
        driftNotificationFormat: data.driftNotificationFormat
      });

      // Update the tenant preferences array
      setTenantPreferences(prev => {
        const existing = prev.findIndex(p => p.tenantId === tenantId);
        if (existing >= 0) {
          const newPrefs = [...prev];
          newPrefs[existing] = updatedPreferences;
          return newPrefs;
        } else {
          return [...prev, updatedPreferences];
        }
      });

      setSuccess(`Preferences saved for ${getTenantDisplayName(tenantId)}.`);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error(`Failed to save tenant preferences for ${tenantId}:`, err);
      setError(`Preferences for ${getTenantDisplayName(tenantId)} did not save. Try again.`);
    } finally {
      setSaving(prev => ({ ...prev, [tenantId]: false }));
    }
  };

  const handleToggleNotifications = (tenantId: string, enabled: boolean) => {
    setFormData(prev => ({
      ...prev,
      [tenantId]: {
        ...prev[tenantId],
        driftNotificationsEnabled: enabled
      }
    }));
  };

  const handleEmailChange = (tenantId: string, email: string) => {
    setFormData(prev => ({
      ...prev,
      [tenantId]: {
        ...prev[tenantId],
        driftNotificationEmail: email
      }
    }));
  };

  const handleFormatChange = (tenantId: string, format: string) => {
    setFormData(prev => ({
      ...prev,
      [tenantId]: {
        ...prev[tenantId],
        driftNotificationFormat: format
      }
    }));
  };

  // Handlers for tenant-level notification settings
  const handleRejectedDriftEmailChange = (tenantId: string, email: string) => {
    setTenantSettingsFormData(prev => ({
      ...prev,
      [tenantId]: {
        ...prev[tenantId],
        rejectedDriftNotificationEmail: email
      }
    }));
  };

  const handleDriftAlertEmailChange = (tenantId: string, email: string) => {
    setTenantSettingsFormData(prev => ({
      ...prev,
      [tenantId]: {
        ...prev[tenantId],
        driftAlertNotificationEmail: email
      }
    }));
  };

  const saveTenantNotificationSettings = async (tenantId: string) => {
    try {
      setSavingTenantSettings(prev => ({ ...prev, [tenantId]: true }));
      setError(null);
      setSuccess(null);

      const data = tenantSettingsFormData[tenantId];
      if (!data) return;

      const updatedSettings = await api.updateTenantNotificationSettings(tenantId, {
        rejectedDriftNotificationEmail: data.rejectedDriftNotificationEmail || undefined,
        driftAlertNotificationEmail: data.driftAlertNotificationEmail || undefined
      });

      setTenantNotificationSettings(prev => ({
        ...prev,
        [tenantId]: updatedSettings
      }));

      setSuccess(`Notification settings saved for ${getTenantDisplayName(tenantId)}.`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error(`Failed to save tenant notification settings for ${tenantId}:`, err);
      setError(`Notification settings for ${getTenantDisplayName(tenantId)} did not save. Try again.`);
    } finally {
      setSavingTenantSettings(prev => ({ ...prev, [tenantId]: false }));
    }
  };

  const handleReconsent = async (tenantId: string) => {
    try {
      setConsentLoading(tenantId);
      const response = await api.initiateAdminConsent(tenantId);
      window.location.href = response.consentUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Consent could not start. Try again.');
      setConsentLoading(null);
    }
  };

  const handleCopyConsentUrl = async (tenantId: string) => {
    try {
      setConsentLoading(tenantId);
      const response = await api.initiateAdminConsent(tenantId);
      try {
        await navigator.clipboard.writeText(response.consentUrl);
        setCopiedTenantId(tenantId);
        setTimeout(() => setCopiedTenantId(null), 2000);
      } catch {
        setError('Copy failed. Allow clipboard access in your browser.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Consent link could not be generated. Try again.');
    } finally {
      setConsentLoading(null);
    }
  };

  const toggleTenantExpanded = (tenantId: string) => {
    setExpandedTenants(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tenantId)) {
        newSet.delete(tenantId);
      } else {
        newSet.add(tenantId);
      }
      return newSet;
    });
  };

  const getTenantDisplayName = (tenantId: string) => {
    const tenant = tenants.find(t => t.tenantId === tenantId);
    return tenant?.name || tenant?.domain || tenantId;
  };

  /** Open a tenant's settings (if closed) and bring them into view: the brief's action. */
  const openTenantSettings = (tenantId: string) => {
    if (!expandedTenants.has(tenantId)) toggleTenantExpanded(tenantId);
    requestAnimationFrame(() => {
      document.getElementById(`settings-tenant-${tenantId}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
  };

  const renderTenantPreferenceCard = (tenantId: string) => {
    const data = formData[tenantId];
    const preferences = tenantPreferences.find(p => p.tenantId === tenantId) || null;
    const isExpanded = expandedTenants.has(tenantId);
    const tenantName = getTenantDisplayName(tenantId);
    const isSaving = saving[tenantId] || false;
    const consent = consentStatuses[tenantId];
    const lastUpdated = preferences ? formatLongDate(preferences.updatedAt) : null;
    const panelId = `settings-panel-${tenantId}`;
    // On a Hybrid tenant only the SharePoint/OneDrive scan path sends drift and
    // rejected-drift emails (DriftImportController, ConfigurationDriftController);
    // drift from Microsoft's configuration monitoring sends none to customer addresses, so the copy
    // must not promise it.
    const emailsSpoOdbOnly = tenants.find(t => t.tenantId === tenantId)?.baselineSource === 'Hybrid';
    const emailScope = emailsSpoOdbOnly ? 'SharePoint and OneDrive ' : '';

    if (!data) return null;

    return (
      <li key={tenantId} id={`settings-tenant-${tenantId}`} className="scroll-mt-4 border-t border-line-subtle first:border-t-0">
        <h3 className="m-0">
          <button
            type="button"
            onClick={() => toggleTenantExpanded(tenantId)}
            aria-expanded={isExpanded}
            aria-controls={panelId}
            className="flex min-h-16 w-full items-center gap-4 px-4 py-3 text-left transition-colors duration-110 ease-carbon hover:bg-layer-hover"
          >
            <span className="min-w-0 flex-1">
              <span className="flex min-w-0 flex-wrap items-center gap-2">
                <span className="text-body font-semibold text-text-primary">{tenantName}</span>
                {preferences && <Tag size="sm">Custom</Tag>}
              </span>
              <span className="mt-0.5 block text-helper font-normal text-text-helper">
                {tenants.find(t => t.tenantId === tenantId)?.domain || 'Tenant-specific notification settings'}
              </span>
            </span>
            <IconChevronDown
              className={`flex-none text-text-primary transition-transform duration-150 ease-carbon ${isExpanded ? 'rotate-180' : ''}`}
            />
          </button>
        </h3>

        {isExpanded && (
          <div id={panelId} role="region" aria-label={`${tenantName} notification settings`} className="flex flex-col gap-8 px-4 pb-8 pt-2">
            {/* Your drift email: the toggle, then the format and the override while it is on. */}
            <SettingsBlock title="Drift notifications" first>
              <div className="flex flex-col gap-6">
                <Toggle
                  label="Drift email"
                  helper={
                    data.driftNotificationsEnabled
                      ? emailsSpoOdbOnly
                        ? `Guardian emails you when a SharePoint or OneDrive scan finds drift in ${tenantName}. Drift in the other workloads shows under Baseline Compliance without an email.`
                        : `Guardian emails you when a scan finds drift in ${tenantName}.`
                      : emailsSpoOdbOnly
                        ? 'Drift notifications are off. Turn them on to get an email for each SharePoint or OneDrive scan that finds drift.'
                        : 'Drift notifications are off. Turn them on to get an email for each scan that finds drift.'
                  }
                  checked={data.driftNotificationsEnabled}
                  onChange={(enabled) => handleToggleNotifications(tenantId, enabled)}
                />

                {/* Notification Format Selection - Only show if notifications are enabled */}
                {data.driftNotificationsEnabled && (
                  <RadioGroup
                    name={`format-${tenantId}`}
                    legend="Notification format"
                    variant="tile"
                    value={data.driftNotificationFormat}
                    onChange={(format) => handleFormatChange(tenantId, format)}
                    options={FORMAT_OPTIONS}
                    className="max-w-2xl"
                  />
                )}

                {/* Email Override Field - Only show if notifications are enabled */}
                {data.driftNotificationsEnabled && (
                  <TextInput
                    type="email"
                    label="Notification email override (optional)"
                    value={data.driftNotificationEmail}
                    onChange={(e) => handleEmailChange(tenantId, e.target.value)}
                    placeholder="Leave empty to use your account email"
                    helper="Notifications for this tenant go here instead of your account email."
                    wrapperClassName="max-w-md"
                  />
                )}

                {/* Save User Preferences Button */}
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <span className="text-helper text-text-helper">
                    {lastUpdated && <>Last updated {lastUpdated}</>}
                  </span>
                  <Button
                    kind="secondary"
                    size="md"
                    onClick={() => saveTenantPreferences(tenantId)}
                    disabled={isSaving}
                    loading={isSaving}
                    icon={<IconCheck />}
                  >
                    {isSaving ? 'Saving' : 'Save preferences'}
                  </Button>
                </div>
              </div>
            </SettingsBlock>

            {/* Permissions & Consent Section - shown for all multi-tenant tenants */}
            {consent?.authenticationMode === 'MultiTenant' && (
              <SettingsBlock title="Permissions and consent">
                <div className="flex flex-col gap-4">
                  {consent.consentStatus?.isValid ? (
                    <div>
                      <p className="m-0 text-body text-text-primary">
                        Admin consent granted
                        {consent.consentStatus.grantedAt && (
                          <span className="ml-1 text-text-secondary">
                            on {formatLongDate(consent.consentStatus.grantedAt) ?? new Date(consent.consentStatus.grantedAt).toLocaleDateString()}
                          </span>
                        )}
                      </p>
                      <p className="m-0 mt-1 text-helper text-text-helper">
                        When Guardian adds a permission, grant consent again to cover it.
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="m-0 flex flex-wrap items-center gap-2 text-body text-text-primary">
                        <Tag kind="high-contrast">Consent missing</Tag>
                        Admin consent is missing or expired.
                      </p>
                      <p className="m-0 mt-1 text-helper text-text-helper">
                        Grant consent again to restore Guardian's access to this tenant.
                      </p>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-4">
                    <Button
                      kind="tertiary"
                      size="md"
                      onClick={() => handleReconsent(tenantId)}
                      disabled={consentLoading === tenantId}
                      loading={consentLoading === tenantId}
                      icon={<IconExternal />}
                    >
                      Update permissions
                    </Button>
                    <Button
                      kind="ghost"
                      size="md"
                      onClick={() => handleCopyConsentUrl(tenantId)}
                      disabled={consentLoading === tenantId}
                      icon={copiedTenantId === tenantId ? <IconCheck /> : <IconCopy />}
                    >
                      {copiedTenantId === tenantId ? 'Copied' : 'Copy consent link'}
                    </Button>
                  </div>
                </div>
              </SettingsBlock>
            )}

            {/* Tenant-Level Settings Section */}
            <SettingsBlock title="Tenant notification settings">
              <p className="m-0 mb-4 text-body text-text-secondary">
                Shared by every user of this tenant. Built for service-desk integrations.
              </p>
              <div className="grid max-w-3xl grid-cols-1 gap-6 c-md:grid-cols-2">
                <TextInput
                  type="email"
                  label="Drift alert email"
                  value={tenantSettingsFormData[tenantId]?.driftAlertNotificationEmail || ''}
                  onChange={(e) => handleDriftAlertEmailChange(tenantId, e.target.value)}
                  placeholder="e.g., alerts@company.com"
                  helper={`New ${emailScope}drifts are emailed here so your service desk can open a ticket.`}
                />
                <TextInput
                  type="email"
                  label="Rejected drift email"
                  value={tenantSettingsFormData[tenantId]?.rejectedDriftNotificationEmail || ''}
                  onChange={(e) => handleRejectedDriftEmailChange(tenantId, e.target.value)}
                  placeholder="e.g., servicedesk@company.com"
                  helper={`Rejected ${emailScope}drifts are emailed here so your service desk can open a ticket.`}
                />
              </div>
              <div className="mt-6 flex justify-end">
                <Button
                  kind="secondary"
                  size="md"
                  onClick={() => saveTenantNotificationSettings(tenantId)}
                  disabled={savingTenantSettings[tenantId]}
                  loading={savingTenantSettings[tenantId]}
                  icon={<IconCheck />}
                >
                  {savingTenantSettings[tenantId] ? 'Saving' : 'Save tenant settings'}
                </Button>
              </div>
            </SettingsBlock>
          </div>
        )}
      </li>
    );
  };

  // ── The brief, from the saved state (never from an unsaved edit) ─────────
  const { brief, lead, need } = composeSettingsBrief(
    tenants.map(t => ({
      tenantId: t.tenantId,
      name: getTenantDisplayName(t.tenantId),
      driftEmailOn: tenantPreferences.find(p => p.tenantId === t.tenantId)?.driftNotificationsEnabled ?? false,
      consentMissing:
        consentStatuses[t.tenantId]?.authenticationMode === 'MultiTenant' &&
        !consentStatuses[t.tenantId]?.consentStatus?.isValid,
    })),
  );
  const loadFailed = !loading && error !== null && tenants.length === 0;

  // The h1 above names the lead tenant, so the button keeps a short label that fits Carbon's 20rem on one
  // line; its accessible name still carries the tenant.
  const primary =
    lead && need === 'consent' ? (
      <Button
        kind="primary"
        icon={<IconExternal />}
        onClick={() => handleReconsent(lead.tenantId)}
        disabled={consentLoading === lead.tenantId}
        loading={consentLoading === lead.tenantId}
        aria-label={`Update permissions for ${lead.name}`}
      >
        Update permissions
      </Button>
    ) : lead && need === 'drift-email' ? (
      <Button
        kind="primary"
        icon={<IconArrowRight />}
        onClick={() => openTenantSettings(lead.tenantId)}
        aria-label={`Set up drift email for ${lead.name}`}
      >
        Set up drift email
      </Button>
    ) : undefined;

  return (
    <div>
      <section aria-labelledby="settings-verdict" className="mt-8">
        <BriefHero
          brief={
            loadFailed
              ? { headline: 'Reload the page to see your settings.', lede: '**Your notification settings did not load**, so Guardian cannot show them.' }
              : brief
          }
          size="pillar"
          headingId="settings-verdict"
          isLoading={loading}
          secondaryAction={loadFailed ? undefined : primary}
        />
      </section>

      <div className="mt-12 flex flex-col gap-8">
        {/* Error Message */}
        {error && !loadFailed && <InlineNotification kind="error" title={error} onClose={() => setError(null)} />}

        {/* Success Message */}
        {success && <ActionFeedback result={{ tone: 'success', message: success }} />}

        {loading ? (
          <Tile padding={24}>
            <Skeleton lines={4} label="Loading settings." />
          </Tile>
        ) : tenants.length > 0 ? (
          <section aria-labelledby="settings-tenants-title" className="bg-layer-1">
            <header className="px-4 pb-4 pt-4">
              <h2 id="settings-tenants-title" className="m-0 text-heading font-normal text-text-primary">
                Notifications by tenant
              </h2>
              <p className="m-0 mt-0.5 text-body text-text-secondary">Email notification preferences, per tenant.</p>
            </header>
            <ul className="m-0 list-none border-t border-line-subtle p-0">
              {/* Tenant-Specific Settings */}
              {tenants.map(tenant => renderTenantPreferenceCard(tenant.tenantId))}
            </ul>
          </section>
        ) : null}

        {/* Info Section */}
        <Tile as="section" aria-labelledby="settings-about-title">
          <TileHeader title="About notification settings" id="settings-about-title" />
          <div className="mt-3 text-body text-text-secondary">
            <p className="m-0">
              Two layers, per tenant:
            </p>
            <ul className="m-0 mt-2 flex list-disc flex-col gap-1 pl-5">
              <li><b className="font-semibold text-text-primary">Your preferences.</b> Drift notifications to you, or to your override address.</li>
              <li><b className="font-semibold text-text-primary">Tenant settings.</b> Drift and rejected-drift emails to your service desk, shared by every user of the tenant.</li>
            </ul>
            <p className="m-0 mt-2">
              On a tenant watched through Microsoft&rsquo;s Tenant Configuration Management, both layers cover SharePoint
              and OneDrive drift only. Drift in Entra, Exchange, Intune, Teams, Defender and Purview shows under Baseline
              Compliance without an email.
            </p>
          </div>
        </Tile>
      </div>
    </div>
  );
}
